import axios from 'axios';

// Base URL for backend API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8093/api';

// Types for authentication
export interface SystemLoginRequest {
  username: string;
  password: string;
}

export interface GoogleLoginRequest {
  googleIdToken: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface AuthResponse {
  userId: number;
  name: string;
  email: string | null;
  username: string | null;
  userType: 'CLIENT' | 'SYSTEM';
  roles: string[];
  privileges: string[];
  accessToken: string;
  refreshToken: string;
  isPremium: boolean;
}

export interface UserProfile {
  userId: number;
  name: string;
  email: string | null;
  profilePhoto: string | null;
  userType: 'CLIENT' | 'SYSTEM';
  roles: string[];
  privileges: string[];
}

export interface SubscriptionResponse {
  isPremium: boolean;
  planName: string | null;
  planPrice: number | null;
  startDate: string | null;
  expiryDate: string | null;
  daysRemaining: number;
  status: string;
  createdDate: string | null;
}

export interface UpdateProfileRequest {
  name: string;
  profilePhotoUrl: string;
}

export interface PlanResponse {
  id: number;
  planName: string;
  durationMonths: number;
  price: number;
  originalPrice: number;
  currency: string;
  discountPercent: number;
  description: string;
  features: string[];
  isActive: boolean;
}

export interface ApiResponse<T> {
  data: T;
  status: string;
  message: string;
  errors: any;
}

// Payment-related interfaces
export interface CreatePaymentOrderRequest {
  planId: number;
}

export interface PaymentOrderResponse {
  orderId: string;
  amount: number;
  currency: string;
  receipt: string;
  key: string;
  userEmail: string;
  userName: string;
  userContact: string;
}

export interface VerifyPaymentRequest {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

export interface PaymentVerificationResponse {
  success: boolean;
  transactionId: string;
  subscriptionId: number;
  planName: string;
  expiryDate: string;
  daysRemaining: number;
}

export interface PaymentTransactionResponse {
  transactionId: string;
  planName: string;
  amount: number;
  currency: string;
  status: string;
  paymentMethod: string;
  createdAt: string;
  completedAt: string;
}

// Axios instance with interceptors
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 error and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await authApi.refreshToken({ refreshToken });

          // Update tokens in localStorage
          localStorage.setItem('accessToken', response.data.accessToken);
          localStorage.setItem('refreshToken', response.data.refreshToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        authApi.clearTokens();
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API functions
export const authApi = {
  /**
   * System user login (username/password)
   */
  async systemLogin(credentials: SystemLoginRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>(
      '/auth/system/login',
      credentials
    );
    return response.data;
  },

  /**
   * Google OAuth2 login
   */
  async googleLogin(request: GoogleLoginRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>(
      '/auth/google/login',
      request
    );
    return response.data;
  },

  /**
   * Refresh access token
   */
  async refreshToken(request: RefreshTokenRequest): Promise<ApiResponse<{accessToken: string, refreshToken: string}>> {
    const response = await apiClient.post<ApiResponse<{accessToken: string, refreshToken: string}>>(
      '/auth/refresh-token',
      request
    );
    return response.data;
  },

  /**
   * Logout
   */
  async logout(): Promise<ApiResponse<null>> {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post<ApiResponse<null>>(
      `${API_BASE_URL}/auth/logout`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    this.clearTokens();
    return response.data;
  },

  /**
   * Get user profile
   */
  async getUserProfile(): Promise<ApiResponse<UserProfile>> {
    const response = await apiClient.get<ApiResponse<UserProfile>>('/user/profile');
    return response.data;
  },

  /**
   * Update user profile
   */
  async updateUserProfile(request: UpdateProfileRequest): Promise<ApiResponse<null>> {
    const response = await apiClient.put<ApiResponse<null>>('/user/profile', request);
    return response.data;
  },

  /**
   * Get subscription status
   */
  async getSubscription(): Promise<ApiResponse<SubscriptionResponse>> {
    const response = await apiClient.get<ApiResponse<SubscriptionResponse>>('/user/subscription');
    return response.data;
  },

  /**
   * Get all active plans (public endpoint - no auth required)
   */
  async getPlans(): Promise<ApiResponse<PlanResponse[]>> {
    const response = await axios.get<ApiResponse<PlanResponse[]>>(`${API_BASE_URL}/plans`);
    return response.data;
  },

  /**
   * Create payment order
   */
  async createPaymentOrder(request: CreatePaymentOrderRequest): Promise<ApiResponse<PaymentOrderResponse>> {
    const response = await apiClient.post<ApiResponse<PaymentOrderResponse>>(
      '/payments/create-order',
      request
    );
    return response.data;
  },

  /**
   * Verify payment
   */
  async verifyPayment(request: VerifyPaymentRequest): Promise<ApiResponse<PaymentVerificationResponse>> {
    const response = await apiClient.post<ApiResponse<PaymentVerificationResponse>>(
      '/payments/verify',
      request
    );
    return response.data;
  },

  /**
   * Get payment history
   */
  async getPaymentHistory(): Promise<ApiResponse<PaymentTransactionResponse[]>> {
    const response = await apiClient.get<ApiResponse<PaymentTransactionResponse[]>>(
      '/payments/history'
    );
    return response.data;
  },

  /**
   * Save tokens to localStorage
   */
  saveTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  },

  /**
   * Clear tokens from localStorage
   */
  clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  },
};

export default authApi;
